import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Wallet, Plus, Trash, X, FloppyDisk } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(n || 0);
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export function PettyCashPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBudget, setShowBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [form, setForm] = useState({ concept: "", amount: "", date: new Date().toISOString().slice(0, 10) });

  const headers = { Authorization: `Bearer ${token}` };
  const m = month || new Date().getMonth() + 1;

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/petty-cash`, { params: { year, month: m }, headers });
      setData(d);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, m, token]);

  useEffect(() => { load(); }, [load]);

  const handleSaveBudget = async () => {
    try {
      await axios.put(`${API}/admin/petty-cash/budget`, { year, month: m, budget: parseFloat(budgetInput) }, { headers });
      toast.success("Presupuesto actualizado"); setShowBudget(false); load();
    } catch { toast.error("Error"); }
  };

  const handleAddExpense = async () => {
    if (!form.concept.trim() || !form.amount) return toast.error("Completa los campos");
    try {
      await axios.post(`${API}/admin/petty-cash/expenses`, { ...form, amount: parseFloat(form.amount), year, month: m }, { headers });
      toast.success("Gasto registrado"); setForm({ concept: "", amount: "", date: new Date().toISOString().slice(0, 10) }); setShowForm(false); load();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este gasto?")) return;
    try { await axios.delete(`${API}/admin/petty-cash/expenses/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  if (!data) return null;

  const pct = data.budget > 0 ? Math.round((data.spent / data.budget) * 100) : 0;
  const isOver = data.remaining < 0;

  return (
    <div className="space-y-4" data-testid="petty-cash-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-slate-700 flex items-center gap-2">
          <Wallet size={20} className="text-amber-600" /> Caja Chica — {MONTHS[m - 1]} {year}
        </h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => { setBudgetInput(String(data.budget || "")); setShowBudget(true); }} data-testid="set-petty-budget">
              Asignar Presupuesto
            </Button>
          )}
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-petty-expense">
            <Plus size={16} className="mr-1" /> Registrar Gasto
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-[10px] uppercase tracking-wide opacity-80 font-medium">Presupuesto</p>
          <p className="text-xl font-bold mt-1">{fmt(data.budget)}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
          <p className="text-[10px] uppercase tracking-wide opacity-80 font-medium">Gastado</p>
          <p className="text-xl font-bold mt-1">{fmt(data.spent)}</p>
        </div>
        <div className={`bg-gradient-to-br ${isOver ? "from-red-600 to-red-700" : "from-emerald-500 to-emerald-600"} rounded-xl p-4 text-white`}>
          <p className="text-[10px] uppercase tracking-wide opacity-80 font-medium">Disponible</p>
          <p className="text-xl font-bold mt-1">{fmt(data.remaining)}</p>
          {data.budget > 0 && (
            <div className="mt-2">
              <div className="w-full h-1.5 bg-white/30 rounded-full">
                <div className={`h-full rounded-full ${pct > 100 ? "bg-red-300" : pct > 80 ? "bg-amber-300" : "bg-white"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <p className="text-[10px] mt-1 opacity-80">{pct}% utilizado</p>
            </div>
          )}
        </div>
      </div>

      {/* Budget dialog */}
      {showBudget && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-600">Presupuesto Caja Chica ($)</label>
            <Input type="number" value={budgetInput} onChange={e => setBudgetInput(e.target.value)} className="mt-1" placeholder="0.00" data-testid="petty-budget-input" />
          </div>
          <Button onClick={handleSaveBudget} size="sm" data-testid="petty-budget-save"><FloppyDisk size={16} className="mr-1" /> Guardar</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowBudget(false)}><X size={16} /></Button>
        </div>
      )}

      {/* Add expense form */}
      {showForm && (
        <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="petty-expense-form">
          <h3 className="text-sm font-semibold text-slate-700">Registrar Gasto de Caja Chica</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Concepto *</label>
              <Input value={form.concept} onChange={e => setForm({ ...form, concept: e.target.value })} className="mt-1" placeholder="Ej: Papeleria" data-testid="petty-concept" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Monto *</label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="0.00" data-testid="petty-amount" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Fecha</label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddExpense} data-testid="petty-save-expense"><FloppyDisk size={16} className="mr-1" /> Guardar</Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X size={16} className="mr-1" /> Cancelar</Button>
          </div>
        </div>
      )}

      {/* Expenses list */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-xs" data-testid="petty-cash-table">
          <thead>
            <tr className="bg-slate-800 text-white">
              <th className="py-2.5 px-3 text-left font-semibold">Fecha</th>
              <th className="py-2.5 px-3 text-left font-semibold">Concepto</th>
              <th className="py-2.5 px-3 text-right font-semibold">Monto</th>
              <th className="py-2.5 px-3 text-left font-semibold">Registrado por</th>
              {isAdmin && <th className="py-2.5 px-3"></th>}
            </tr>
          </thead>
          <tbody>
            {data.expenses?.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-slate-400">Sin gastos registrados</td></tr>
            ) : data.expenses?.map(e => (
              <tr key={e.id} className="border-t hover:bg-slate-50/70">
                <td className="py-2.5 px-3 text-slate-600">{e.date}</td>
                <td className="py-2.5 px-3 font-medium text-slate-800">{e.concept}</td>
                <td className="py-2.5 px-3 text-right font-semibold text-red-600">{fmt(e.amount)}</td>
                <td className="py-2.5 px-3 text-slate-500">{e.created_by_name}</td>
                {isAdmin && (
                  <td className="py-2.5 px-3">
                    <button onClick={() => handleDelete(e.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
